import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, IndianRupee, Users, Target, AlertTriangle, RefreshCw, Loader2, Search,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import TruncatedText from '../../../components/ui/truncated-text';

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const money = (n) => (n === null || n === undefined ? '—' : `₹ ${formatINR(n)}`);

// Matches OUTCOME_MAP in engage/analytics.py. Keeping the vocabulary identical
// on both sides means a status added to the backend shows up here as "working"
// rather than silently vanishing from the counts.
const OUTCOMES = [
  { key: 'converted', label: 'Converted', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
  { key: 'working',   label: 'In progress', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  { key: 'untouched', label: 'Not yet worked', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  { key: 'lost',      label: 'Lost', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
];

const PLATFORMS = [
  { value: '',              label: 'Meta (Facebook + Instagram)' },
  { value: 'meta_ads',      label: 'Facebook Ads only' },
  { value: 'instagram_ads', label: 'Instagram Ads only' },
];

const Stat = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-gray-400" />
      <span className="text-[10px] font-medium uppercase text-gray-500">{label}</span>
    </div>
    <div className={cn('mt-1 text-xl font-bold tabular-nums', tone || 'text-gray-900 dark:text-white')}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
  </div>
);

export default function MetaPerformanceReport() {
  const { fetch: apiFetch } = useApiMethods();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [platform, setPlatform] = useState('');
  const [bucket, setBucket] = useState('');
  const [search, setSearch] = useState('');
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      if (platform) qs.append('platform', platform);
      if (bucket) qs.append('bucket', bucket);
      if (search.trim()) qs.append('search', search.trim());
      const url = `engage/analytics/meta-performance/${qs.toString() ? `?${qs}` : ''}`;
      const resp = await apiFetch(url);
      if (resp?.status === 'success') setData(resp.data);
      else setError(resp?.message || 'Failed to load report');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, from, to, platform, bucket, search]);

  useEffect(() => { load(); }, [tick]);       // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {                            // debounce filter changes
    const t = setTimeout(() => setTick(x => x + 1), 350);
    return () => clearTimeout(t);
  }, [from, to, platform, bucket, search]);

  const t = data?.totals;
  const campaigns = data?.campaigns || [];
  const rows = data?.enquiries || [];

  const attributionRate = useMemo(() => {
    if (!t || !t.enquiries) return null;
    return Math.round(((t.enquiries - t.unattributed) / t.enquiries) * 100);
  }, [t]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Link to="/engage/reports" className="mt-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Meta Performance</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Patient enquiries produced by Facebook and Instagram campaigns, and what happened to them
            </p>
          </div>
        </div>
        <button onClick={() => setTick(x => x + 1)} disabled={loading}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} /> Refresh
        </button>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-gray-500 dark:text-gray-400">
          From
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                 className="ml-1 h-8 rounded-md border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        </label>
        <label className="text-xs text-gray-500 dark:text-gray-400">
          To
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
                 className="ml-1 h-8 rounded-md border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        </label>
        <select value={platform} onChange={e => setPlatform(e.target.value)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
          {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Name, mobile, email, department…"
                 className="h-8 w-60 rounded-md border border-gray-200 bg-white pl-7 pr-2 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        </div>
      </div>

      {error && (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : !t ? null : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat icon={Users} label="Enquiries" value={t.enquiries} />
            <Stat icon={Target} label="Converted" value={t.converted} sub={`${t.conversion_rate}% of enquiries`} />
            <Stat icon={IndianRupee} label="Spend" value={money(t.spend)} />
            <Stat icon={IndianRupee} label="Cost / enquiry" value={money(t.cost_per_enquiry)} />
            <Stat icon={IndianRupee} label="Cost / conversion" value={money(t.cost_per_conversion)} />
            <Stat icon={IndianRupee} label="Expected value" value={money(t.expected_value)}
                  sub={t.roas ? `${t.roas}× spend` : undefined} />
          </div>

          {t.unattributed > 0 && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" />
              <span>
                <strong>{t.unattributed} of {t.enquiries}</strong> digital enquiries carry no campaign
                {attributionRate !== null && <> — only {attributionRate}% are attributed</>}. Cost per
                enquiry and conversion only count the attributed ones, so both read higher than reality.
                Pick a campaign when creating an enquiry to fix this going forward.
              </span>
            </div>
          )}

          {/* per campaign */}
          <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] uppercase text-gray-500 dark:border-gray-700">
                  <th className="px-4 py-2.5 font-medium">Campaign</th>
                  <th className="px-4 py-2.5 font-medium">Platform</th>
                  <th className="px-4 py-2.5 font-medium">Enquiries</th>
                  <th className="px-4 py-2.5 font-medium">Converted</th>
                  <th className="px-4 py-2.5 font-medium">Lost</th>
                  <th className="px-4 py-2.5 font-medium">Conv %</th>
                  <th className="px-4 py-2.5 font-medium">Spend</th>
                  <th className="px-4 py-2.5 font-medium">CPL</th>
                  <th className="px-4 py-2.5 font-medium">CPA</th>
                  <th className="px-4 py-2.5 font-medium">Expected value</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.campaign_id ?? 'unattributed'}
                      className={cn('border-b border-gray-50 last:border-0 dark:border-gray-700/50',
                                    c.is_unattributed && 'bg-amber-50/50 dark:bg-amber-950/10')}>
                    <td className="max-w-[220px] px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">
                      <TruncatedText text={c.campaign_name} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">{c.platform_display}</td>
                    <td className="px-4 py-2.5 tabular-nums">{c.enquiries}</td>
                    <td className="px-4 py-2.5 tabular-nums text-teal-600 dark:text-teal-400">{c.converted}</td>
                    <td className="px-4 py-2.5 tabular-nums text-red-500">{c.lost}</td>
                    <td className="px-4 py-2.5 tabular-nums">{c.conversion_rate}%</td>
                    <td className="px-4 py-2.5 tabular-nums">{money(c.spend)}</td>
                    <td className="px-4 py-2.5 tabular-nums">{money(c.cost_per_enquiry)}</td>
                    <td className="px-4 py-2.5 tabular-nums">{money(c.cost_per_conversion)}</td>
                    <td className="px-4 py-2.5 tabular-nums">{money(c.expected_value)}</td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No Meta campaigns in this period. Add one under Digital Marketing.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* outcome filter */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setBucket('')}
                    className={cn('rounded-full px-3 py-1 text-[11px] font-medium',
                                  !bucket ? 'bg-blue-600 text-white'
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300')}>
              All ({t.enquiries})
            </button>
            {OUTCOMES.map(o => (
              <button key={o.key} onClick={() => setBucket(bucket === o.key ? '' : o.key)}
                      className={cn('rounded-full px-3 py-1 text-[11px] font-medium',
                                    bucket === o.key ? 'bg-blue-600 text-white'
                                                     : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300')}>
                {o.label} ({t[o.key]})
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-400">
              {rows.length} shown{data.detail_truncated && ' (first 500)'}
            </span>
          </div>

          {/* enquiry detail */}
          <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[10px] uppercase text-gray-500 dark:border-gray-700">
                  <th className="px-4 py-2.5 font-medium">Created</th>
                  <th className="px-4 py-2.5 font-medium">Patient</th>
                  <th className="px-4 py-2.5 font-medium">Mobile No</th>
                  <th className="px-4 py-2.5 font-medium">Campaign</th>
                  <th className="px-4 py-2.5 font-medium">Department</th>
                  <th className="px-4 py-2.5 font-medium">Stage</th>
                  <th className="px-4 py-2.5 font-medium">Outcome</th>
                  <th className="px-4 py-2.5 font-medium">Assigned To</th>
                  <th className="px-4 py-2.5 font-medium">Expected value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const oc = OUTCOMES.find(o => o.key === r.outcome);
                  return (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0 dark:border-gray-700/50">
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                        {r.created_date ? new Date(r.created_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="max-w-[170px] px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">
                        <TruncatedText text={r.name} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-600 dark:text-gray-400">{r.mobile || '—'}</td>
                      <td className="max-w-[170px] px-4 py-2.5 text-gray-600 dark:text-gray-400">
                        <TruncatedText text={r.campaign_name} />
                      </td>
                      <td className="max-w-[140px] px-4 py-2.5 text-gray-600 dark:text-gray-400">
                        <TruncatedText text={r.department} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{r.stage || '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', oc?.cls)}>
                          {oc?.label || r.status}
                        </span>
                      </td>
                      <td className="max-w-[140px] px-4 py-2.5 text-gray-600 dark:text-gray-400">
                        <TruncatedText text={r.assigned_to} />
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{r.expected_value ? money(r.expected_value) : '—'}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No enquiries match these filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-gray-400">
            Expected value is the treatment value recorded on each enquiry, not realised revenue —
            it counts what the enquiry is worth if it converts. Spend is what was entered against the
            campaign under Digital Marketing; nothing is pulled from Meta.
          </p>
        </>
      )}
    </div>
  );
}
